import React from "react";
import { StarRating } from "./StarRating";

export interface Testimonial {
  id: number | string;
  name: string;
  avatar: string;
  rating: number;
  comment: string;
  date?: string;
}

interface TestimonialsProps {
  testimonials: Testimonial[];
  sectionId?: string;
  badgeText?: string;
  title?: string;
  subtitle?: string;
  columns?: 1 | 2 | 3 | 4;
}

export default function Testimonials({
  testimonials,
  sectionId = "testimonials",
  badgeText = "Testimonials",
  title = "What Our Customers Say",
  subtitle = "Real reviews from real customers who love shopping with us",
  columns = 3,
}: TestimonialsProps) {
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <section
      id={sectionId}
      className="container mx-auto max-w-7xl px-4 py-20"
    >
      <div className="text-center mb-12">
        <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
          {badgeText}
        </span>

        <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
          {title}
        </h2>

        <p className="font-secondary text-gray-500 max-w-2xl mx-auto">
          {subtitle}
        </p>
      </div>

      <div className={`grid ${gridCols[columns]} gap-6`}>
        {testimonials.map((testimonial) => (
          <div
            key={testimonial.id}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center gap-4 mb-4">
              <img
                src={testimonial.avatar}
                alt={testimonial.name}
                className="w-14 h-14 rounded-full object-cover"
              />

              <div>
                <h4 className="font-primary font-semibold text-gray-800">
                  {testimonial.name}
                </h4>

                <StarRating rating={testimonial.rating} />
              </div>
            </div>

            <p className="font-secondary text-gray-600 leading-relaxed">
              "{testimonial.comment}"
            </p>

            {testimonial.date && (
              <p className="font-secondary text-xs text-gray-400 mt-4">
                {testimonial.date}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}